import { SubmittedEvidenceItem } from '../types/RequirementSteps.types';
import { parseDateValue, toStartOfDayISOString } from './Date.util';

// Backend returns evidence newest-first (orderBy createdAt desc), so the first
// item is the latest submission.
export const latestEvidence = (
  items?: SubmittedEvidenceItem[] | null
): SubmittedEvidenceItem | undefined => items?.[0];

export const formatEvidenceDate = (value?: string): string =>
  value ? new Date(value).toLocaleString() : '';

// Converts a `<input type="date">` value (a bare YYYY-MM-DD) into the ISO instant we send as
// `attestationDate`.
//
// parseDateValue reads the bare date at LOCAL midnight, so the instant we send is the day the user
// actually picked — a plain `new Date(value)` parses as UTC midnight, which resolves to the PREVIOUS
// day west of UTC. Total by design: returns undefined rather than throwing on an unparseable value,
// which jsdom (and a browser without a native date picker) can produce.
//
// Shared by the url and document evidence controls so the two cannot drift.
export const toAttestationDate = (value: string): string | undefined => {
  const parsed = parseDateValue(value);
  return parsed ? toStartOfDayISOString(parsed) : undefined;
};

// Today as a `<input type="date">` value, for capping the picker via `max`. Built from local
// components for the same reason as above: `toISOString().slice(0, 10)` is yesterday's date for
// anyone west of UTC late in the day.
export const todayDateInputValue = (now: Date = new Date()): string => {
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// True when a `<input type="date">` value is after today, in LOCAL time (matching parseDateValue,
// which reads a bare YYYY-MM-DD at local midnight, and todayDateInputValue above).
//
// The pair to todayDateInputValue: that one caps the picker via `max`, this one catches the
// keyboard-entered value that bypasses the cap. They sit together deliberately - EAA-1937 was a
// control that had neither while its sibling had both. The server enforces this too; validating
// here is just faster, and lets the message land on the date field instead of arriving as a toast.
//
// Compared against the END of today so a same-day submission is accepted whatever the clock says.
// `now` is injectable for tests, matching todayDateInputValue's signature. Total, like
// toAttestationDate: an unparseable value is NOT future-dated - toAttestationDate drops it later.
export const futureDated = (value: string, now: Date = new Date()): boolean => {
  const parsed = parseDateValue(value);
  if (!parsed) return false;
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  return parsed.getTime() > endOfToday.getTime();
};

// One source of copy for the date rules, so a client-side guard and the server rejection for the
// same rule cannot word themselves differently. The label is lowercased mid-sentence ("Audit date"
// -> "the audit date cannot..."); the default yields exactly "The date cannot be in the future."
export const futureDateMessage = (label = 'Date'): string =>
  `The ${label.toLowerCase()} cannot be in the future.`;

export const dateRequiredMessage = (label = 'Date'): string =>
  `Enter the ${label.toLowerCase()}.`;

// Safe reader for `valueJson` string fields (url / attestedAt / attestedById),
// which is typed `unknown`.
export const evidenceField = (
  item: SubmittedEvidenceItem | undefined,
  key: string
): string | undefined => {
  const value = item?.valueJson;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const field = (value as Record<string, unknown>)[key];
    if (typeof field === 'string') return field;
  }
  return undefined;
};
